/**
 * <ec-cart-note>
 *
 * Light-DOM Web Component for the order note (`cart.note`). Rendered by
 * snippets/ec-cart-note.liquid on two surfaces:
 *   - cart page: the textarea carries `name='note'`, so the native POST /cart
 *     form already persists it. This module only adds save-as-you-type.
 *   - cart drawer: no enclosing form, so every save goes through
 *     /cart/update.js — debounced on input, immediate on blur, and flushed
 *     by <ec-cart-drawer> before it navigates to checkout or the cart page.
 *
 * Saves never dispatch CartUpdateEvent: the drawer re-opens itself on that
 * event when `auto-open` is set, so typing a note on the cart page would
 * make the drawer pop up. A dedicated `ec:cart-note:saved` event on
 * `document` keeps the surfaces in sync instead — required, not cosmetic:
 * on /cart both textareas coexist, and a stale `name='note'` on the page
 * one would overwrite what the buyer just typed in the drawer.
 *
 * No Shadow DOM. Idempotent define. No imports.
 */

const DEBOUNCE_MS = 700;
const SAVED_EVENT = 'ec:cart-note:saved';
const DEFAULT_UPDATE_URL = '/cart/update.js';

class EcCartNote extends HTMLElement {
  #input = null;
  #status = null;
  #form = null;
  #updateUrl = DEFAULT_UPDATE_URL;
  #lastSaved = '';
  #timer = null;
  #inFlight = null;
  #onSaved = null;
  #onSubmit = null;
  #baselined = false;

  connectedCallback() {
    this.#input = this.querySelector('[data-ec-cart-note-input]');
    this.#status = this.querySelector('[data-ec-cart-note-status]');
    if (!(this.#input instanceof HTMLTextAreaElement)) return;

    this.#updateUrl = this.dataset.updateUrl || DEFAULT_UPDATE_URL;
    // Baseline once per instance. connectedCallback re-runs on every DOM
    // re-insertion (theme-editor re-render, `theme dev` hot reload, any move),
    // and by then the textarea may already hold text the buyer typed but that
    // was never POSTed. Re-baselining would mark it clean, so the dirty check
    // in flush() would swallow it and neither blur nor checkout would save it.
    if (!this.#baselined) {
      this.#lastSaved = this.#input.value;
      this.#baselined = true;
    }

    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('blur', this.#onBlur);

    this.#onSaved = (event) => {
      if (event.detail?.source === this) return;
      this.syncFromCart(event.detail?.note);
    };
    document.addEventListener(SAVED_EVENT, this.#onSaved);

    // The native form POST already carries `name='note'`; a queued XHR would
    // race it and could land after the redirect with a stale value.
    this.#form = this.closest('form');
    this.#onSubmit = () => this.cancelPending();
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    // A disconnect can be permanent (removal) or temporary (re-render, move).
    // Either way a debounced note still owes a POST: cancelling it here would
    // drop what the buyer typed. flush() clears the timer itself, is a no-op
    // when nothing is dirty, and always resolves — fire-and-forget is safe.
    this.flush();
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('blur', this.#onBlur);
    if (this.#onSaved) document.removeEventListener(SAVED_EVENT, this.#onSaved);
    if (this.#onSubmit) this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  /**
   * Cancels the debounce and saves the current value if it is dirty.
   * Always resolves — callers await it before navigating, and a network
   * failure must not strand the buyer on the page.
   */
  async flush() {
    this.cancelPending();
    if (!this.#input) return;
    // Let the in-flight POST settle so the dirty check below sees its result.
    if (this.#inFlight) await this.#inFlight;
    const value = this.#input.value;
    if (value === this.#lastSaved) return;
    await this.#save(value);
  }

  /** Adopts a note coming from another surface or from /cart.js. */
  syncFromCart(note) {
    if (!this.#input) return;
    const value = typeof note === 'string' ? note : '';
    // Double guard: never stomp what the buyer is writing, nor a value that
    // hasn't been sent yet.
    if (document.activeElement === this.#input) return;
    if (this.#timer || this.#inFlight) return;
    if (this.#input.value === value) return;
    this.#input.value = value;
    this.#lastSaved = value;
  }

  cancelPending() {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #onInput = () => {
    this.cancelPending();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.flush();
    }, DEBOUNCE_MS);
  };

  #onBlur = () => {
    this.flush();
  };

  #save(value) {
    const previous = this.#lastSaved;
    // Optimistic so a concurrent flush doesn't re-POST the same value.
    this.#lastSaved = value;

    const run = (async () => {
      try {
        const res = await fetch(this.#updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
        });
        if (!res.ok) throw new Error(`cart/update failed: ${res.status}`);
        this.#announce(this.dataset.i18nSaved);
        document.dispatchEvent(
          new CustomEvent(SAVED_EVENT, { detail: { note: value, source: this } })
        );
      } catch (err) {
        console.error('[ec-cart-note] note save failed', err);
        // Stay dirty so a later flush (blur, checkout) retries.
        this.#lastSaved = previous;
        this.#announce(this.dataset.i18nError);
      }
    })();

    this.#inFlight = run;
    // Cleared outside the async body so `run` is never touched before it is
    // assigned (a synchronous fetch throw would hit its temporal dead zone).
    run.then(() => {
      if (this.#inFlight === run) this.#inFlight = null;
    });
    return run;
  }

  #announce(text) {
    if (this.#status && text) this.#status.textContent = text;
  }
}

if (!customElements.get('ec-cart-note')) {
  customElements.define('ec-cart-note', EcCartNote);
}
